import { StButton } from "@storytell/ui/buttons";
import { TbX } from "solid-icons/tb";
import { type Component, For, Show } from "solid-js";
import { TagInput } from "./TagInput";
import type { EmailInvite, Role } from "./types";

interface CreateEmailInviteFormProps {
  newEmailInvites: EmailInvite[];
  pendingEmails: string[];
  currentEmailInput: string;
  selectedRole: Role;
  errorMessage: string;
  availableRoles?: Role[]; // Optional: if provided, only show these roles in dropdown
  onPendingEmailsChange: (emails: string[]) => void;
  onCurrentInputChange: (input: string) => void;
  onRoleChange: (role: Role) => void;
  onAddEmails: () => void;
  onRemoveEmail: (id: string) => void;
  onUpdateEmailRole: (id: string, role: Role) => void;
  onSubmit: () => void;
  onClose: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const CreateEmailInviteForm: Component<CreateEmailInviteFormProps> = (props) => {
  const isAddButtonDisabled = () => {
    const hasValidPending =
      props.pendingEmails.length > 0 && props.pendingEmails.every((email) => EMAIL_REGEX.test(email.trim()));
    const hasValidCurrent = props.currentEmailInput.trim() && EMAIL_REGEX.test(props.currentEmailInput.trim());
    return !hasValidPending && !hasValidCurrent;
  };

  return (
    <div class="p-4 bg-canvas border border-primary-border rounded-lg space-y-3">
      <div class="flex items-center justify-between">
        <h4 class="text-sm font-medium">Invite by Email</h4>
        <StButton onClick={props.onClose} class="p-1 hover:bg-canvas-hover rounded">
          <TbX class="w-4 h-4" />
        </StButton>
      </div>

      {/* Email invite list */}
      <Show when={props.newEmailInvites.length > 0}>
        <div class="space-y-2">
          <For each={props.newEmailInvites}>
            {(invite) => (
              <div class="flex items-center gap-2 p-2 bg-panel rounded-lg border border-primary-border">
                <span class="text-sm flex-1 truncate">{invite.email}</span>
                <select
                  value={invite.role}
                  onChange={(e) => props.onUpdateEmailRole(invite.id, e.currentTarget.value as Role)}
                  class="px-2 py-1 text-xs bg-canvas border border-primary-border rounded focus:outline-none focus:ring-2 focus:ring-brand"
                  disabled={props.availableRoles && props.availableRoles.length === 1}
                >
                  <For each={props.availableRoles || ["Owner", "Collaborator", "Reader"]}>
                    {(role) => <option value={role}>{role}</option>}
                  </For>
                </select>
                <button
                  type="button"
                  onClick={() => props.onRemoveEmail(invite.id)}
                  class="p-1 hover:bg-canvas-hover rounded transition-colors"
                >
                  <TbX class="w-4 h-4 text-muted-text" />
                </button>
              </div>
            )}
          </For>
        </div>
      </Show>

      {/* Email error message */}
      <Show when={props.errorMessage}>
        <div class="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p class="text-sm text-red-500">{props.errorMessage}</p>
        </div>
      </Show>

      {/* Add email input */}
      <div class="flex gap-2">
        <TagInput
          placeholder="Mark@MyCompany.com, Margaux@MyClient.com, etc..."
          value={props.pendingEmails}
          onValueChange={props.onPendingEmailsChange}
          onInputChange={props.onCurrentInputChange}
          onAdd={async () => {
            // Check if we have pending emails before processing
            const hadPendingEmails = props.pendingEmails.length > 0;
            // Process the pending emails (this will add them to newEmailInvites)
            props.onAddEmails();
            // Submit if we had pending emails (meaning emails were processed)
            if (hadPendingEmails) {
              // Use a small delay to ensure state updates are complete
              setTimeout(async () => {
                await props.onSubmit();
              }, 50);
            }
          }}
          validator={(email) => EMAIL_REGEX.test(email.trim())}
          class="flex-1"
        />
        <select
          value={props.selectedRole}
          onChange={(e) => props.onRoleChange(e.currentTarget.value as Role)}
          class="px-3 py-2 bg-panel border border-primary-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          disabled={props.availableRoles && props.availableRoles.length === 1}
        >
          <For each={props.availableRoles || ["Owner", "Collaborator", "Reader"]}>
            {(role) => <option value={role}>{role}</option>}
          </For>
        </select>
        <button
          type="button"
          onClick={async () => {
            props.onAddEmails();
            await props.onSubmit();
          }}
          disabled={isAddButtonDisabled()}
          class="px-4 py-2 bg-brand text-on-brand hover:bg-brand-hover border border-primary-border rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add
        </button>
      </div>
    </div>
  );
};
